import { trackError } from '@billy/track-error'
import React, { memo, ReactElement, useCallback, useMemo, useState } from 'react'
import { Document, DocumentProps, Page, PageProps, pdfjs } from 'react-pdf'

import { Image } from '../../../Image'
import { usePdfPreload } from '../../contexts/pdfPreloadContext'
import { DocumentLocalProps, FileComponentProps, FileType, Orientation } from '../../types'
import { getFileType } from '../../utils/getFileType'
import { FileErrorThumbnail, FileLockedThumbnail } from '../FileThumbnails'
import { LIMIT_CANVAS_WIDTH_CLASS_NAME } from './constants/limitCanvasWidthClassName'
import * as Styled from './styles'

// Required by the react-pdf library:
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`

const documentOptions: DocumentProps['options'] = {
  cMapUrl: `//cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/cmaps/`,
  cMapPacked: true,
}

export const SCALE_PORTRAIT_X2 = 2
export const SCALE_LANDSCAPE_X4 = 4

type ErrorType = 'default' | 'password-protected'

export const PdfReact = memo((props: FileComponentProps): ReactElement | null => {
  const {
    activeFileType,
    activePage,
    aspectRatio,
    fitType,
    fitToHeight,
    height,
    onError,
    onLoad,
    onPdfLoad,
    src,
    width,
    withAnnotations = true,
  } = props
  const [orientation, setOrientation] = useState<Orientation>('portrait')
  const [error, setError] = useState<ErrorType>()
  const { setIsLoading } = usePdfPreload()

  const isPortraitOrientation = orientation === 'portrait'
  const isError = !!error

  const pageHeight = useMemo(() => (fitToHeight && height) || undefined, [fitToHeight, height])

  const srcFileType = activeFileType ?? getFileType(src)

  const handleLoad = useCallback(
    (pdf: pdfjs.PDFDocumentProxy) => {
      const { numPages } = pdf

      const checkOrientation = async () => {
        let orientation: Orientation = 'portrait'

        const page = await pdf.getPage(1)
        // Page viewport contains dimensions
        const viewport = page.getViewport({ scale: 1.0 })

        orientation = viewport.width > viewport.height ? 'landscape' : 'portrait'

        setOrientation(orientation)
        onLoad?.(orientation)
      }

      setIsLoading(false)
      onPdfLoad(numPages)
      checkOrientation()
    },
    [onLoad, onPdfLoad, setIsLoading],
  )

  const handlePassword = useCallback(() => {
    trackError(`Pdf ${src} is password protected!`)
    setError('password-protected')
  }, [src])

  const handleLoadError = useCallback(
    (error: Error) => {
      error.message = `Error while loading pdf file: ${src}. ${error.message}`
      trackError(error)
      setError('default')
      onError?.()
    },
    [onError, src],
  )

  const handleSourceError = useCallback(
    (error: Error) => {
      error.message = `Error while getting pdf file: ${src}. ${error.message}`
      trackError(error)
      setError('default')
      onError?.()
    },
    [onError, src],
  )

  const documentProps: DocumentLocalProps = useMemo(
    () => ({
      options: documentOptions,
      externalLinkTarget: '_blank',
    }),
    [],
  )

  const pageProps: PageProps = useMemo(() => {
    /* PDFs are rendered with scale x2 (portrait) or x4 (landscape) to ensure good quality on zoom */
    const scale = isPortraitOrientation ? SCALE_PORTRAIT_X2 : SCALE_LANDSCAPE_X4
    const computedWidth = isPortraitOrientation && pageHeight ? 0 : width

    return {
      height: pageHeight,
      pageIndex: activePage,
      renderAnnotationLayer: withAnnotations,
      scale,
      width: computedWidth,
    }
  }, [activePage, isPortraitOrientation, pageHeight, width, withAnnotations])

  const previewDocumentProps: DocumentLocalProps = useMemo(
    () => ({
      ...documentProps,
      onPassword: handlePassword,
      onLoadError: handleLoadError,
      onLoadProgress: () => setIsLoading(true),
      onLoadSuccess: handleLoad,
      onSourceError: handleSourceError,
      externalLinkTarget: '_blank',
      className: LIMIT_CANVAS_WIDTH_CLASS_NAME,
    }),
    [documentProps, handleLoad, handleLoadError, handlePassword, handleSourceError, setIsLoading],
  )

  const PdfComponent = useMemo(() => {
    if (srcFileType === FileType.Pdf) {
      return (
        <Document file={src} {...previewDocumentProps}>
          <Page key={pageProps.scale?.toString()} {...pageProps} />
        </Document>
      )
    }
  }, [pageProps, previewDocumentProps, src, srcFileType])

  const RenderedComponent = useMemo(
    () => (srcFileType === FileType.Pdf ? PdfComponent : <Image src={src} objectFit={fitType} />),
    [PdfComponent, src, srcFileType, fitType],
  )

  if (!RenderedComponent) {
    return null
  }

  return (
    <Styled.PdfReactWrapper aspectRatio={aspectRatio} height={pageHeight} isError={isError}>
      {isError ? (
        <Styled.ErrorWrapper>
          {error === 'password-protected' ? <FileLockedThumbnail /> : <FileErrorThumbnail />}
        </Styled.ErrorWrapper>
      ) : (
        <Styled.ScaleWrapper>{RenderedComponent}</Styled.ScaleWrapper>
      )}
    </Styled.PdfReactWrapper>
  )
})
